import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getTitle } from '../helpers';

/**
 * One option row. Replaces the legacy MUI `<MenuItem>` + `<Checkbox>`
 * + `<Tooltip>` stack with a flat row that uses the design-system
 * `.cb-box` checkbox style and a hover-only quick-apply action.
 *
 * Behaviour preserved 1:1:
 *   - Clicking the row toggles the option (was MUI MenuItem onClick).
 *   - Clicking the lightning-bolt button applies the current draft
 *     INCLUDING this option immediately (single-click "select +
 *     apply"). We stop event propagation so the row toggle below
 *     doesn't double-fire.
 *   - The `getTitle` localiser still resolves any composite ids
 *     (comma-joined codes → friendly labels via SCIT/SLIT/formula
 *     dictionaries).
 *   - Tooltip-on-overflow logic (compare offsetWidth vs scrollWidth)
 *     is dropped — the row sits in a fixed-width pane, the label is
 *     ellipsed, and the user can read the full value by selecting it
 *     into the right pane. Re-introducing a tooltip here cluttered
 *     the popover during scrolling and the legacy implementation
 *     never re-measured on resize anyway.
 */
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const BoltIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const MinusIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/**
 * `state` is 'none' | 'include' | 'exclude' — where this option sits in
 * the draft. It is what makes the active mode visible at the point of
 * action rather than only in the mode switch: an excluded option draws a
 * red minus box instead of a blue tick, so a user who forgot which mode
 * they are in reads it off the row itself.
 *
 * It is optional so callers that have no notion of exclusion (the right
 * pane's include group) can keep passing `selected` alone.
 */
const MenuRow = ({ option, selected = [], state, onItemClick, applyCurrentFilter }) => {
  const { scit, slit, formulas, tables } = useSelector(reduxState => reduxState.i18n);
  const titleRef = useRef();
  const rowState = state || (selected.includes(option) ? 'include' : 'none');
  const isChecked = rowState !== 'none';
  const isExcluded = rowState === 'exclude';

  const onRowClick = () => onItemClick(option, !isChecked);

  const onQuickActionClick = e => {
    e.preventDefault();
    e.stopPropagation();
    applyCurrentFilter(e);
  };

  const title = useMemo(() => {
    const names = option.split(',');
    return getTitle(names, { scit, slit, formulas, tables });
  }, [option]);

  return (
    <div
      className={`qhs-dynh-row${isExcluded ? ' qhs-dynh-row--excluded' : ''}`}
      role="option"
      aria-selected={isChecked}
      onClick={onRowClick}
    >
      <label className="qhs-dynh-row__cb" onClick={e => e.stopPropagation()}>
        <input type="checkbox" checked={isChecked} onChange={onRowClick} />
        <span className="qhs-dynh-row__box">{isExcluded ? <MinusIcon /> : <CheckIcon />}</span>
      </label>
      <span className="qhs-dynh-row__label" ref={titleRef}>
        {title}
      </span>
      <button
        type="button"
        className="qhs-dynh-row__qa"
        onClick={onQuickActionClick}
        aria-label={tables?.applyOnly || 'Apply only this'}
        title={tables?.applyOnly || 'Apply only this'}
      >
        <BoltIcon />
      </button>
    </div>
  );
};

export default MenuRow;
